<template>
  <v-container fluid :aria-busy="loading ? 'true' : 'false'">
    <v-card class="elevation-2 mb-4">
      <v-toolbar color="primary" dark flat>
        <v-btn icon @click="back" title="Volver a concesionarios" aria-label="Volver a concesionarios">
          <v-icon aria-hidden="true">mdi-arrow-left</v-icon>
        </v-btn>
        <v-icon class="mr-2" aria-hidden="true">mdi-store</v-icon>
        <v-toolbar-title>Detalles del concesionario</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="load" :disabled="loading" title="Actualizar" aria-label="Actualizar datos">
          <v-icon aria-hidden="true">mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>
    </v-card>

    <!-- Carga inicial: esqueleto con la misma estructura para que la página no salte -->
    <template v-if="loading && !dealer">
      <v-skeleton-loader type="list-item-avatar-two-line" class="elevation-1 mb-4"></v-skeleton-loader>
      <v-row>
        <v-col cols="12" md="4"><v-skeleton-loader type="card, list-item-two-line@3" class="elevation-1"></v-skeleton-loader></v-col>
        <v-col cols="12" md="8"><v-skeleton-loader type="table-heading, table-tbody" class="elevation-1"></v-skeleton-loader></v-col>
      </v-row>
    </template>

    <v-alert v-if="notFound" type="warning" text>
      No se encontró el concesionario en tu grupo.
      <template v-slot:append>
        <v-btn small text color="warning" @click="back">Volver al listado</v-btn>
      </template>
    </v-alert>

    <template v-if="dealer">
      <!-- Cabecera: identidad del concesionario y contadores -->
      <v-card class="elevation-1 mb-4">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="7">
              <div class="d-flex align-center">
                <v-avatar color="primary" size="56" class="mr-4"><v-icon dark size="30" aria-hidden="true">mdi-store</v-icon></v-avatar>
                <div class="min-width-0">
                  <h1 class="text-h5 font-weight-medium text--primary dealer-name">{{ dealer.name }}</h1>
                  <div class="text-body-2 grey--text text--darken-1" v-if="dealer.business_name">{{ dealer.business_name }}</div>
                  <div class="mt-2 d-flex flex-wrap align-center">
                    <v-chip small class="mr-1 mb-1" :color="dealer.status === STATUS_ACTIVE ? 'success' : 'grey darken-1'" dark>
                      <v-icon left x-small aria-hidden="true">{{ dealer.status === STATUS_ACTIVE ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
                      {{ dealer.status === STATUS_ACTIVE ? 'Activo' : 'Inactivo' }}
                    </v-chip>
                    <v-chip v-for="c in dealer.conditions" :key="c" small class="mr-1 mb-1" color="primary" outlined>{{ conditionLabel(c) }}</v-chip>
                    <span v-if="!dealer.conditions.length" class="grey--text text--darken-1 text-caption">Sin condiciones</span>
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="5">
              <v-row dense>
                <v-col cols="4" v-for="stat in stats" :key="stat.label">
                  <component
                    :is="stat.tab !== undefined ? 'button' : 'div'"
                    :type="stat.tab !== undefined ? 'button' : undefined"
                    class="stat-tile text-center"
                    :class="{ 'stat-tile--link': stat.tab !== undefined, 'stat-tile--active': stat.tab !== undefined && tab === stat.tab }"
                    :aria-label="stat.tab !== undefined ? `${stat.label}: ${stat.value}. Ver ${stat.label.toLowerCase()}` : `${stat.label}: ${stat.value}`"
                    @click="stat.tab !== undefined && (tab = stat.tab)"
                  >
                    <v-icon small :color="stat.color" aria-hidden="true">{{ stat.icon }}</v-icon>
                    <div class="text-h5 font-weight-bold stat-value">{{ stat.value }}</div>
                    <div class="caption grey--text text--darken-1">{{ stat.label }}</div>
                  </component>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row>
        <!-- Ficha del concesionario -->
        <v-col cols="12" md="4">
          <v-card class="elevation-1 fill-height">
            <v-card-title class="pb-1 text-subtitle-1 font-weight-medium">
              <v-icon left color="primary" aria-hidden="true">mdi-information</v-icon>
              Información
            </v-card-title>
            <v-divider></v-divider>
            <v-list dense class="py-2">
              <v-list-item v-for="field in dealerFields" :key="field.label" class="detail-item">
                <v-list-item-icon class="mr-3"><v-icon color="primary" small aria-hidden="true">{{ field.icon }}</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="caption grey--text text--darken-1">{{ field.label }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 text--primary detail-value">
                    <a v-if="field.value && field.href" :href="field.href" class="text-decoration-none">{{ field.value }}</a>
                    <template v-else>{{ field.value || '—' }}</template>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- Ubicaciones y usuarios -->
        <v-col cols="12" md="8">
          <v-card class="elevation-1">
            <v-tabs v-model="tab" show-arrows>
              <v-tab>
                <v-icon left small aria-hidden="true">mdi-map-marker</v-icon>
                Ubicaciones
                <v-chip x-small class="ml-2">{{ locations.length }}</v-chip>
              </v-tab>
              <v-tab>
                <v-icon left small aria-hidden="true">mdi-account-group</v-icon>
                Usuarios
                <v-chip x-small class="ml-2">{{ users.length }}</v-chip>
              </v-tab>
            </v-tabs>
            <v-divider></v-divider>

            <v-tabs-items v-model="tab">
              <v-tab-item>
                <div class="d-flex pa-3 pb-0">
                  <v-text-field v-model="searchLocations" label="Buscar ubicación" prepend-inner-icon="mdi-magnify"
                    clearable outlined dense hide-details single-line class="table-search"></v-text-field>
                </div>
                <div class="table-wrap">
                  <v-data-table :headers="locationHeaders" :items="locations" :search="searchLocations" item-key="id"
                    :loading="loadingLocations" :items-per-page="10" dense
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }" no-results-text="Ninguna ubicación coincide con la búsqueda">
                    <template v-slot:item.name="{ item }">
                      <div class="font-weight-medium">{{ item.name }}</div>
                      <div class="text-caption grey--text text--darken-1" v-if="item.type">{{ item.type }}</div>
                    </template>
                    <template v-slot:item.address="{ item }">
                      {{ locationAddress(item) }}
                    </template>
                    <template v-slot:item.phone="{ item }">
                      <a v-if="item.phone" :href="`tel:${item.phone}`" class="text-decoration-none">{{ item.phone }}</a>
                      <span v-else>—</span>
                    </template>
                    <template v-slot:item.dealers="{ item }">
                      <v-chip v-for="d in (item.dealers || [])" :key="d.id" x-small class="mr-1 mb-1"
                        :color="d.id === item.dealer_id ? 'primary' : undefined" :outlined="d.id === item.dealer_id">{{ d.name }}</v-chip>
                      <v-chip x-small v-if="(item.dealers || []).length > 1" color="deep-purple" dark>
                        <v-icon left x-small aria-hidden="true">mdi-share-variant</v-icon>Compartida
                      </v-chip>
                    </template>
                    <template v-slot:no-data>
                      <div class="empty-state">
                        <v-icon size="40" color="grey lighten-1" aria-hidden="true">mdi-map-marker-off-outline</v-icon>
                        <div class="subtitle-2 mt-2">Este concesionario aún no tiene ubicaciones</div>
                        <div class="caption grey--text text--darken-1 mb-3">Puedes crearlas y asignarlas desde la sección Ubicaciones.</div>
                        <v-btn small outlined color="primary" :to="{ name: 'GrupoUbicaciones' }">Ir a Ubicaciones</v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="d-flex pa-3 pb-0">
                  <v-text-field v-model="searchUsers" label="Buscar usuario" prepend-inner-icon="mdi-magnify"
                    clearable outlined dense hide-details single-line class="table-search"></v-text-field>
                </div>
                <div class="table-wrap">
                  <v-data-table :headers="userHeaders" :items="users" :search="searchUsers" item-key="id"
                    :loading="loadingUsers" :items-per-page="10" dense
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }" no-results-text="Ningún usuario coincide con la búsqueda">
                    <template v-slot:item.name="{ item }">
                      <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
                      <a :href="`mailto:${item.email}`" class="text-caption text-decoration-none">{{ item.email }}</a>
                    </template>
                    <template v-slot:item.phone="{ item }">
                      <a v-if="item.phone" :href="`tel:${item.phone}`" class="text-decoration-none">{{ item.phone }}</a>
                      <span v-else>—</span>
                    </template>
                    <template v-slot:item.role="{ item }">
                      <v-chip x-small label :color="userRole(item) === 'manager' ? 'deep-purple' : 'blue-grey'" dark>
                        {{ userRoleLabel(item) }}
                      </v-chip>
                    </template>
                    <template v-slot:item.conditions="{ item }">
                      <v-chip v-for="c in (item.conditions || [])" :key="c" x-small class="mr-1" color="primary" outlined>{{ conditionLabel(c) }}</v-chip>
                    </template>
                    <template v-slot:item.status="{ item }">
                      <v-chip x-small :color="item.status === STATUS_ACTIVE ? 'success' : 'grey darken-1'" dark>
                        <v-icon left x-small aria-hidden="true">{{ item.status === STATUS_ACTIVE ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
                        {{ item.status === STATUS_ACTIVE ? 'Activo' : 'Inactivo' }}
                      </v-chip>
                    </template>
                    <template v-slot:no-data>
                      <div class="empty-state">
                        <v-icon size="40" color="grey lighten-1" aria-hidden="true">mdi-account-off-outline</v-icon>
                        <div class="subtitle-2 mt-2">Este concesionario aún no tiene usuarios</div>
                        <div class="caption grey--text text--darken-1 mb-3">Puedes darlos de alta desde la sección Usuarios.</div>
                        <v-btn small outlined color="primary" :to="{ name: 'GrupoUsers' }">Ir a Usuarios</v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import GrupoPanelService from '@/services/GrupoPanelService'
import LocationService from '@/services/LocationService'
import { DEALER_ROLES, STATUS_ACTIVE, roleLabel, conditionLabel, apiData, apiError } from '@/grupo'

// Entorno Grupo: ficha de un concesionario del grupo con sus datos, todas sus ubicaciones y sus usuarios
export default {
  name: 'GrupoDealerDetail',

  props: {
    dealer_id: { type: [String, Number], required: true }
  },

  data: () => ({
    STATUS_ACTIVE,
    dealer: null,
    users: [],
    locations: [],
    tab: 0,
    searchLocations: '',
    searchUsers: '',
    loading: false,
    loadingUsers: false,
    loadingLocations: false,
    notFound: false,
    locationHeaders: [
      { text: 'Ubicación', value: 'name' },
      { text: 'Dirección', value: 'address', sortable: false },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Dealers', value: 'dealers', sortable: false }
    ],
    userHeaders: [
      { text: 'Usuario', value: 'name' },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Rol', value: 'role', sortable: false },
      { text: 'Condiciones', value: 'conditions', sortable: false },
      { text: 'Estado', value: 'status', align: 'center' }
    ]
  }),

  computed: {
    dealerFields () {
      const d = this.dealer
      return [
        { icon: 'mdi-card-account-details-outline', label: 'CIF', value: d.cif },
        { icon: 'mdi-city', label: 'Población', value: d.city },
        { icon: 'mdi-map-marker', label: 'Dirección', value: d.address },
        { icon: 'mdi-phone', label: 'Teléfono', value: d.phone, href: d.phone ? `tel:${d.phone}` : null },
        { icon: 'mdi-email', label: 'Correo electrónico', value: d.email, href: d.email ? `mailto:${d.email}` : null }
      ]
    },

    // `tab`: pestaña a la que lleva el contador (los que no tienen no son interactivos)
    stats () {
      return [
        { icon: 'mdi-account-group', color: 'primary', label: 'Usuarios', value: this.users.length, tab: 1 },
        { icon: 'mdi-map-marker', color: 'teal darken-1', label: 'Ubicaciones', value: this.locations.length, tab: 0 },
        { icon: 'mdi-car', color: 'deep-purple', label: 'Vehículos', value: this.dealer.vehicles_count !== undefined ? this.dealer.vehicles_count : '—' }
      ]
    }
  },

  watch: {
    dealer_id () {
      this.load()
    }
  },

  mounted () {
    this.load()
  },

  methods: {
    conditionLabel,

    back () {
      this.$router.push({ name: 'GrupoDealers' })
    },

    userRole (user) {
      return (user.roles || []).find(r => DEALER_ROLES.some(d => d.value === r)) || null
    },

    userRoleLabel (user) {
      const role = this.userRole(user)
      return role ? roleLabel(role) : '—'
    },

    locationAddress (location) {
      const parts = [location.address, location.city, location.zip ? `(${location.zip})` : null].filter(Boolean)
      return parts.join(', ') || '—'
    },

    async load () {
      this.loading = true
      this.notFound = false
      try {
        // El listado trae también el nº de vehículos; si el dealer no es del grupo no aparece
        const dealers = apiData(await GrupoPanelService.dealers()) || []
        this.dealer = dealers.find(d => String(d.id) === String(this.dealer_id)) || null
        if (!this.dealer) {
          this.notFound = true
          this.users = []
          this.locations = []
          return
        }
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo cargar el concesionario'))
        return
      } finally {
        this.loading = false
      }
      this.loadUsers()
      this.loadLocations()
    },

    async loadUsers () {
      this.loadingUsers = true
      try {
        this.users = apiData(await GrupoPanelService.users({ dealer_id: this.dealer.id })) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los usuarios'))
      } finally {
        this.loadingUsers = false
      }
    },

    // Todas las ubicaciones del dealer (también las compartidas), recorriendo todas las páginas
    async loadLocations () {
      this.loadingLocations = true
      try {
        const all = []
        let page = 1
        let pageCount = 1
        do {
          const response = await LocationService.get({
            expand: 'dealers',
            'filter[dealer_id]': this.dealer.id,
            'per-page': 50,
            page
          })
          all.push(...(apiData(response) || []))
          const meta = response.data._meta || {}
          pageCount = meta.pageCount || 1
          page++
        } while (page <= pageCount)
        this.locations = all
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar las ubicaciones'))
      } finally {
        this.loadingLocations = false
      }
    }
  }
}
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

.dealer-name {
  line-height: 1.2;
  overflow-wrap: anywhere;
}

/* Contadores: botón accesible que lleva a su pestaña */
.stat-tile {
  display: block;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 8px 4px;
  background: transparent;
  font: inherit;
  color: inherit;
}

.stat-tile--link {
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.stat-tile--link:hover {
  background-color: rgba(25, 118, 210, 0.06);
  border-color: rgba(25, 118, 210, 0.4);
}

.stat-tile--link:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.stat-tile--active {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.08);
}

.stat-value {
  font-variant-numeric: tabular-nums;
}

.detail-item {
  min-height: 52px;
}

.detail-value {
  white-space: normal;
  overflow-wrap: anywhere;
}

.table-search {
  max-width: 320px;
}

/* Altura mínima para que el contenido no salte al cambiar de pestaña o buscar */
.table-wrap {
  min-height: 300px;
  overflow-x: auto;
}

.empty-state {
  text-align: center;
  padding: 32px 16px;
}

@media (prefers-reduced-motion: reduce) {
  .stat-tile--link {
    transition: none;
  }
}
</style>
